import React, { ComponentProps, useCallback, useContext, useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  getDurationPackageOptions,
  OfferPackageOptionView,
} from 'selectors/offerPage/offerPageSelectors'
import TextButton from 'components/Luxkit/Button/TextButton'
import {
  CALENDAR_GA_EVENT_ACTION,
  CALENDAR_GA_EVENT_LABEL,
  OFFER_PAGE_BOOKING_GA_EVENT_ACTION,
  OFFER_PAGE_BOOKING_GA_EVENT_LABEL,
} from 'constants/analytics'
import { fireEcommerceGTMEvent, fireInteractionEvent } from 'api/googleTagManager'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { addToCartGTM, calendarAction, upgradeModalEvent } from 'analytics/eventDefinitions'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import { isLoggedIn as isLoggedInSelector } from 'selectors/accountSelectors'

import { useAppDispatch } from 'hooks/reduxHooks'
import config from 'constants/config'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { getBookingPath } from 'lib/url/pathsUtils'
import { cartInitializePackage } from 'actions/CartActions'
import { RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { STD_BOOKING_STEPS, STEP_DEPARTING_FLIGHT } from 'constants/booking'
import { pushWithRegion } from 'actions/NavigationActions'
import { OfferUpgradeModalContext } from './OfferUpgradeModalContext'
import { addToCheckoutCart, clearCheckoutCart } from 'actions/CheckoutActions'
import { generateAccommodationCheckoutItems, generateVillaCheckoutItem } from 'lib/checkout/accommodation/cart'
import { useCreateTripItemBatch, useTrip } from 'tripPlanner/hooks/api'
import GeoContext from 'contexts/geoContext'
import { createLEHotelItemPayload } from 'tripPlanner/utils/itemConversions/cart/leHotel'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { isExperiencesEnabled } from 'selectors/cart/cartSelectors'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { countOccupantsForFlights, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getUpgradeablePackage } from './utils'
import { generateFlightCheckoutItem } from 'lib/checkout/flights/cart'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import { FlightViewTypes } from 'constants/flight'
import { buildParamsKey } from 'lib/offer/availabilityUtils'
import { isLpcHotel, isVillaOffer } from 'lib/offer/offerTypes'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import { addToCart } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'

interface Props extends Omit<ComponentProps<typeof TextButton>, 'kind'> {
  airport?: App.Airport;
  checkIn: moment.Moment;
  isLoggedIn: boolean;
  offer: App.Offer;
  packageOptions: Array<OfferPackageOptionView>;
  packageView: OfferPackageOptionView;
  rooms: Array<App.RoomOccupants>;
  offerAvailableRates?: App.OfferAvailableRatesByOccupancy;
  onTransitionToBooking?: () => void;
  onSubmit?: () => void;
  tripPlannerTripId?: string;
  experiencesEnabled: boolean;
  reserveForZero?: boolean;
  overrideRooms?: Array<App.RoomOccupants | App.Occupants>;
}

function OfferBookButton(props: Props) {
  const {
    children,
    packageView,
    rooms,
    isLoggedIn,
    checkIn,
    packageOptions,
    offer,
    airport,
    offerAvailableRates,
    onTransitionToBooking,
    onSubmit,
    tripPlannerTripId,
    experiencesEnabled,
    ...rest
  } = props
  const { data: currentTrip } = useTrip({ tripId: tripPlannerTripId })
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const { mutate: createTripItems } = useCreateTripItemBatch()
  const dispatch = useAppDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const offerPageDispatch = useOfferPageDispatch()
  const showOfferPackageUpgradeModal = useContext(OfferUpgradeModalContext)
  const showModal = useContext(ModalContext)
  // We are passed a package view, but that's not always guarenteed to be the 'latest' one
  // e.g. a `selectedPackageView` doesn't include price updates based on date selection (maybe there was a surcharge?)
  // So to get around this, we always use the 'latest' calculated view for the unique key we're given
  const latestPackageView = useMemo(() => packageOptions.find(option => option.package.uniqueKey === packageView.package.uniqueKey), [packageOptions, packageView.package.uniqueKey])
  const transitionToBooking = useCallback((option: OfferPackageOptionView, flights: boolean) => {
    dispatch(clearCheckoutCart())
    const checkOut = checkIn.clone().add(option.package.duration, 'days')
    const offerAvailableRatesKey = buildParamsKey(checkIn.format(ISO_DATE_FORMAT), checkOut.format(ISO_DATE_FORMAT), rooms)
    const availableRate = offerAvailableRates?.[offerAvailableRatesKey]?.rates?.find(rate => rate.packageUniqueKey === option.package.uniqueKey)

    // Temporary tracking for Book Now btn usage
    const buyType = flights ? OFFER_PAGE_BOOKING_GA_EVENT_LABEL.PackageOptionFlightBookNow : OFFER_PAGE_BOOKING_GA_EVENT_LABEL.PackageOptionBookNow
    fireInteractionEvent({
      category: 'book-now-hotel',
      action: OFFER_PAGE_BOOKING_GA_EVENT_ACTION.BookNowBookingFlowStarted,
      label: buyType,
    })

    onTransitionToBooking?.()

    dispatch(cartInitializePackage(offer, option.package, RESERVATION_TYPE_INSTANT_BOOKING, {
      availableRate,
      endDate: checkOut.format(ISO_DATE_FORMAT),
      flightPrice: flights ? option.flightPrice : undefined,
      occupancies: rooms,
      price: option.basePrice / rooms.length,
      surcharge: option.surcharge / rooms.length,
      roomPrices: option.roomPrices,
      startDate: checkIn.format(ISO_DATE_FORMAT),
      taxesAndFees: Math.ceil(option.taxesAndFees / rooms.length),
      propertyFees: (option.propertyFees ?? 0) / rooms.length,
      availableRooms: option.availableRooms,
    }) as any,
    )

    let bookingPath = ''

    if (flights) {
      bookingPath = getBookingPath(STEP_DEPARTING_FLIGHT, {
        departDate: checkIn.format(ISO_DATE_FORMAT),
        destinationAirportCode: offer.flightDestinationPort,
        numberOfNights: option.package.duration,
        offerId: offer.id,
        originAirportCode: airport?.code,
        packageId: option.package.id,
        reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
        returnDate: checkOut.format(ISO_DATE_FORMAT),
        withFlights: 'true',
        ...(offer.forceBundleId && { forceBundleId: offer.forceBundleId }),
      })
    } else {
      let startIdx = 0
      if (!experiencesEnabled) { startIdx++ }
      bookingPath = getBookingPath(STD_BOOKING_STEPS[startIdx], {
        reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
        packageId: option.package.id,
        offerId: offer.id,
        originAirportCode: airport?.code,
        numberOfNights: option.package.duration,
      })
    }

    dispatch(pushWithRegion(bookingPath) as any)
  }, [airport?.code, checkIn, dispatch, offer, offerAvailableRates, onTransitionToBooking, rooms, experiencesEnabled])

  const proceedToCheckout = useCallback((packageOption: OfferPackageOptionView | undefined, flights: boolean) => {
    if (!packageOption) { return }
    const checkOut = checkIn.clone().add(packageOption.package.duration, 'days')
    const dates = { checkIn: checkIn.format(ISO_DATE_FORMAT), checkOut: checkOut.format(ISO_DATE_FORMAT) }

    let accommodationItems: Array<App.Checkout.InstantBookingLEHotelItem> = []
    if (offer.type !== 'rental') {
      accommodationItems = generateAccommodationCheckoutItems(offer, packageOption, dates, rooms, currentCurrency)
    }

    let villaItems: Array<App.Checkout.VillaItem> = []
    if (offer.type === 'rental') {
      villaItems = [generateVillaCheckoutItem(offer, packageOption, dates, rooms[0], currentCurrency)]
    }

    const flightItems: Array<App.Checkout.FlightItem> = []
    if (flights && airport) {
      const flightOccupants = countOccupantsForFlights(rooms)
      flightItems.push(generateFlightCheckoutItem({
        originAirportCode: airport.code,
        destinationAirportCode: offer.flightDestinationPort,
        bundledItemIds: accommodationItems.map(item => item.itemId),
        occupants: flightOccupants,
        fareType: 'return',
        viewType: FlightViewTypes.TWO_ONE_WAYS,
        passengers: generatePassengersFromOccupants(flightOccupants),
        quotedFare: packageOption.flightPrice / rooms.length,
      }))
    }
    dispatch(addToCheckoutCart([...accommodationItems, ...villaItems, ...flightItems]))
    const addToCartEvent = addToCart()
    if (addToCartEvent) Analytics.track('add-to-cart', addToCartEvent)
    dispatch(navigateToCheckout())
  }, [checkIn, offer, rooms, currentCurrency, airport, dispatch])

  const onBookNow = useCallback((packageOption: OfferPackageOptionView, flights: boolean) => {
    offerPageDispatch({ type: OfferPageActions.closeFilter })

    const saveToCurrentTrip = (packageOption: OfferPackageOptionView) => {
      if (currentTrip) {
        const payload = createLEHotelItemPayload({
          offer,
          pkg: packageOption.package,
          startDate: checkIn.format(ISO_DATE_FORMAT),
          endDate: checkIn.clone().add(packageOption.package.duration, 'days').format(ISO_DATE_FORMAT),
          occupancies: rooms.map(room => ({
            adults: room.adults,
            children: room.children,
            infants: room.infants,
            childrenAge: room.childrenAge,
          })),
          currentRegionCode,
          currencyCode: currentCurrency,
          price: packageOption.hotelPrice + (packageOption.propertyFees || 0),
          value: packageOption.hotelValue + (packageOption.propertyFees || 0),
        })
        createTripItems({ tripId: currentTrip.id, tripItems: [payload] })
      }
    }

    let upgradeAvailable: OfferPackageOptionView | undefined
    if (
      config.PACKAGE_UPGRADE_MODAL_ENABLED &&
      !isVillaOffer(offer) && !isLpcHotel(offer)
    ) {
      upgradeAvailable = getUpgradeablePackage(packageOptions, packageOption)
    }

    if (upgradeAvailable) {
      fireInteractionEvent(upgradeModalEvent(
        'open-modal',
        `${packageOption.package.id}, ${upgradeAvailable.package.id}`,
        offer.analyticsType,
      ))
      trackEvent('UPGRADE_MODAL_OPENED')
      showOfferPackageUpgradeModal(upgradeAvailable, packageOption, flights).then((resolvedPackageOption: OfferPackageOptionView) => {
        fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, resolvedPackageOption?.hotelPrice, sumUpOccupancies(rooms), 'hotel'))
        if (!flights || (config.UNIVERSAL_CHECKOUT_INFLOW_FLIGHTS_ENABLED && (!offer.bundledWithFlightsOnly || config.UNIVERSAL_CHECKOUT_FORCED_BUNDLE_ENABLED))) {
          proceedToCheckout(resolvedPackageOption, flights)
        } else {
          transitionToBooking(resolvedPackageOption, flights)
        }
        saveToCurrentTrip(resolvedPackageOption)
      }).catch(() => {
        // do nothing because the user closed the modal by clicking outside
      })
      return
    }

    fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, packageOption?.hotelPrice, sumUpOccupancies(rooms), 'hotel'))
    if (!flights || (config.UNIVERSAL_CHECKOUT_INFLOW_FLIGHTS_ENABLED && (!offer.bundledWithFlightsOnly || config.UNIVERSAL_CHECKOUT_FORCED_BUNDLE_ENABLED))) {
      proceedToCheckout(packageOption, flights)
    } else {
      transitionToBooking(packageOption, flights)
    }
    saveToCurrentTrip(packageOption)
  }, [offerPageDispatch, currentTrip, packageOptions, offer, currentCurrency, rooms, checkIn, currentRegionCode, createTripItems, trackEvent, showOfferPackageUpgradeModal, proceedToCheckout, transitionToBooking])

  const invalidRoomSelection = !rooms || isRoomsInvalid(rooms)

  const onBookNowClick = useCallback(async(e: React.MouseEvent<HTMLButtonElement>) => {
    onSubmit?.()

    const isFlightBuyType = e.currentTarget.dataset.buytype === 'flights'
    const buyType: { calendar: string, capacity: string, bookNow: string } = isFlightBuyType ?
        {
          calendar: CALENDAR_GA_EVENT_LABEL.PackageOptionFlightAvailability,
          capacity: OFFER_PAGE_BOOKING_GA_EVENT_LABEL.PackageOptionFlightAvailability,
          bookNow: OFFER_PAGE_BOOKING_GA_EVENT_LABEL.PackageOptionFlightBookNow,
        } :
        {
          calendar: CALENDAR_GA_EVENT_LABEL.PackageOptionAvailability,
          capacity: OFFER_PAGE_BOOKING_GA_EVENT_LABEL.PackageOptionAvailability,
          bookNow: OFFER_PAGE_BOOKING_GA_EVENT_LABEL.PackageOptionBookNow,
        }
    if (invalidRoomSelection) {
      // Temporary implementation of tracking for opening of the guest capacity select box
      fireInteractionEvent({
        category: 'capacity-hotel',
        action: OFFER_PAGE_BOOKING_GA_EVENT_ACTION.OpenFilter,
        label: buyType.capacity,
      })

      e.preventDefault()
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'capacity',
        room: latestPackageView.package.roomType,
        flights: isFlightBuyType,
        selectedPackageView: latestPackageView,
      })
      return
    }

    if (!checkIn) {
      fireInteractionEvent(calendarAction(offer.analyticsType, CALENDAR_GA_EVENT_ACTION.OpenFilter, buyType.calendar))
      e.preventDefault()
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'checkin',
        room: latestPackageView.package.roomType,
        flights: isFlightBuyType,
        selectedPackageView: latestPackageView,
      })

      return
    }

    if (!isLoggedIn && !offer.walledGarden) {
      e.preventDefault()
      offerPageDispatch({ type: OfferPageActions.closeFilter })
      const hotelOfferDetails: App.AccountModalOfferDetails | undefined = latestPackageView && {
        title: offer.property?.name || latestPackageView.package.name,
        duration: latestPackageView.package.duration,
        value: isFlightBuyType ? latestPackageView.hotelPrice + latestPackageView.flightPrice : latestPackageView.hotelPrice,
        imageId: latestPackageView.package.roomType?.images[0].id,
        imageTitle: offer.property?.name || latestPackageView.package.name,
        discountPercentage: isFlightBuyType ? 0 : latestPackageView.hotelDiscountPercent,
        isBundledWithFlights: isFlightBuyType,
      }
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal
        initialMode="login"
        hotelOfferDetails={hotelOfferDetails}
      />)

      if (result.loggedIn) {
        onBookNow(latestPackageView, isFlightBuyType)
      }
      return
    }
    onBookNow(latestPackageView, isFlightBuyType)
  }, [onSubmit, invalidRoomSelection, checkIn, isLoggedIn, offer, onBookNow, latestPackageView, offerPageDispatch, showModal])

  return (
    <TextButton {...rest} kind="primary" onClick={onBookNowClick}>
      {children}
    </TextButton>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps) => ({
  airport: state.airport,
  checkIn: state.checkInDate,
  isLoggedIn: isLoggedInSelector(appState),
  offer: state.offer,
  offerAvailableRates: appState.offer.offerAvailableRates[state.offer.id],
  packageOptions: getDurationPackageOptions(state, appState),
  rooms: ownProps.overrideRooms ?? state.rooms,
  tripPlannerTripId: appState.tripPlanner.currentTripId,
  experiencesEnabled: isExperiencesEnabled(appState),
}))(OfferBookButton)
